/**
 * OAuth Callback Page
 *
 * Handles the OAuth callback and exchanges code for token
 */

import { useEffect, useState, useRef } from "react";
import { useAuthStore } from "@/stores/auth-store";
import authService from "@/services/auth-service";

export default function CallbackPage() {
  const [error, setError] = useState<string | null>(null);
  const { setUser, setToken } = useAuthStore();
  const hasRun = useRef(false);

  useEffect(() => {
    // Prevent double execution in React StrictMode
    if (hasRun.current) return;
    hasRun.current = true;

    const handleCallback = async () => {
      try {
        // Get authorization code from URL
        const params = new URLSearchParams(window.location.search);
        const code = params.get("code");
        const errorParam = params.get("error");

        if (errorParam) {
          setError(`Authentication failed: ${errorParam}`);
          return;
        }

        if (!code) {
          setError("No authorization code received");
          return;
        }

        // Exchange code for token
        const user = await authService.handleCallback(code);

        // Update store
        setUser(user);
        const token = localStorage.getItem("auth_token");
        if (token) {
          setToken(token);
        }

        // Redirect to dashboard
        window.location.href = "/";
      } catch (err) {
        console.error("Callback error:", err);
        setError(err instanceof Error ? err.message : "Authentication failed");
      }
    };

    handleCallback();
  }, [setUser, setToken]);

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 p-4">
        <div className="w-full max-w-md rounded-lg bg-white p-8 shadow-lg">
          <div className="text-center">
            <div className="mb-4 inline-block rounded-full bg-red-100 p-4">
              <svg
                className="h-12 w-12 text-red-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </div>
            <h2 className="mb-2 text-2xl font-bold text-gray-900">
              Authentication Failed
            </h2>
            <p className="mb-6 text-gray-600">{error}</p>
            <button
              onClick={() => (window.location.href = "/login")}
              className="rounded-lg bg-blue-600 px-6 py-2 text-white transition-colors hover:bg-blue-700"
            >
              Back to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 p-4">
      <div className="text-center">
        <div className="mb-4 inline-block h-16 w-16 animate-spin rounded-full border-b-4 border-t-4 border-blue-600"></div>
        <h2 className="mb-2 text-2xl font-semibold text-gray-900">
          Authenticating...
        </h2>
        <p className="text-gray-600">Please wait while we sign you in</p>
      </div>
    </div>
  );
}
